"use client";

import { useEffect, useId, useRef, useState } from "react";
import type {
  BuilderTranslationPublishLabels,
  BuilderTranslationPublishLanguage,
} from "./translationTypes";

export type BuilderTranslationPublishDialogProps = {
  busy: boolean;
  labels: BuilderTranslationPublishLabels;
  languages: BuilderTranslationPublishLanguage[];
  onClose: () => void;
  onPublishTranslations: BuilderTranslationPublishHandler;
  onPublishWholePage: BuilderTranslationPublishHandler;
  onSelectionChange: (languageCodes: Set<string>) => void;
  open: boolean;
  selectedLanguageCodes: ReadonlySet<string>;
};

export type BuilderTranslationPublishHandler = {
  (): Promise<void> | void;
};

export function BuilderTranslationPublishDialog({
  busy,
  labels,
  languages,
  onClose,
  onPublishTranslations,
  onPublishWholePage,
  onSelectionChange,
  open,
  selectedLanguageCodes,
}: BuilderTranslationPublishDialogProps) {
  const titleId = useId();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const interactionBusy = busy || submitting;

  useEffect(() => {
    if (!open) {
      return;
    }

    document.body.style.overflow = "hidden";

    function closeDialog(event: KeyboardEvent): void {
      if (event.key === "Escape" && !interactionBusy) {
        onClose();
      }
    }

    window.addEventListener("keydown", closeDialog);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", closeDialog);
    };
  }, [interactionBusy, onClose, open]);

  if (!open) {
    return null;
  }

  function toggle(languageCode: string): void {
    const next = new Set(selectedLanguageCodes);

    if (next.has(languageCode)) {
      next.delete(languageCode);
    } else {
      next.add(languageCode);
    }

    onSelectionChange(next);
  }

  async function publish(action: BuilderTranslationPublishHandler): Promise<void> {
    if (submittingRef.current || busy) {
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    try {
      await action();
    } catch {
      // The owner presents publishing errors.
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[150] grid place-items-center bg-black/65 p-4 backdrop-blur-sm"
      data-builder-translation-publish-dialog="true"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target && !interactionBusy) {
          onClose();
        }
      }}
    >
      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className="w-full max-w-lg rounded-xl border border-white/10 bg-[#171820] p-5 text-slate-100 shadow-2xl"
        role="dialog"
      >
        <h2 className="text-base font-semibold" id={titleId}>
          {labels.title}
        </h2>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          {labels.description}
        </p>

        {languages.length === 0 ? (
          <div className="mt-4 rounded-md border border-white/10 bg-white/[0.03] p-3 text-sm text-slate-400">
            {labels.noTranslations}
          </div>
        ) : (
          <fieldset className="mt-4 space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {labels.languages}
            </legend>
            {languages.map((language) => (
              <label
                className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.025] px-3 py-2.5 transition-colors hover:border-cyan-400/35 hover:bg-cyan-400/[0.05]"
                key={language.code}
              >
                <span className="flex items-center gap-3">
                  <input
                    checked={selectedLanguageCodes.has(language.code)}
                    className="accent-cyan-400"
                    disabled={interactionBusy}
                    onChange={() => toggle(language.code)}
                    type="checkbox"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-100">
                      {language.name}
                    </span>
                    <span className="block text-xs uppercase text-slate-500">
                      {language.code}
                    </span>
                  </span>
                </span>
                <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold text-amber-200">
                  {labels.draft}
                </span>
              </label>
            ))}
          </fieldset>
        )}

        <p className="mt-4 rounded-md border border-sky-400/20 bg-sky-400/[0.08] px-3 py-2 text-xs leading-5 text-sky-200">
          {labels.translationOnlyNotice}
        </p>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="h-9 rounded-md border border-white/10 px-3 text-xs font-semibold text-slate-300 hover:bg-white/[0.05] disabled:opacity-45"
            disabled={interactionBusy}
            onClick={onClose}
          >
            {labels.cancel}
          </button>
          <button
            type="button"
            className="h-9 rounded-md border border-white/10 bg-white/[0.035] px-3 text-xs font-semibold text-slate-200 hover:border-cyan-400/35 hover:bg-cyan-400/[0.08] disabled:opacity-45"
            disabled={interactionBusy}
            onClick={() => void publish(onPublishWholePage)}
          >
            {labels.publishWholePage}
          </button>
          <button
            type="button"
            className="h-9 rounded-md bg-cyan-400 px-3 text-xs font-bold text-slate-950 hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-45"
            disabled={interactionBusy || selectedLanguageCodes.size === 0}
            onClick={() => void publish(onPublishTranslations)}
          >
            {labels.publishTranslations}
          </button>
        </div>
      </section>
    </div>
  );
}
